import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle, Send, Loader2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { SITE } from "../config/site";

const EMPTY = { name: "", phone: "", level: "", subject: "", mode: "", message: "" };

const inputCls =
    "w-full bg-[#0F172A]/60 border border-slate-700 text-slate-100 px-4 py-3 text-sm placeholder:text-slate-600 focus:outline-none focus:border-sky-400 transition-colors duration-300";

const Contact = () => {
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
            toast.success("Richiesta inviata! Ti richiamiamo al più presto.");
            setForm(EMPTY);
        } catch {
            toast.error("Qualcosa è andato storto. Chiamaci direttamente, è più veloce!");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contatti" className="relative py-32 sm:py-40 border-t border-slate-800">
            <div className="max-w-[1440px] mx-auto px-6 sm:px-10">
                <SectionHeading
                    index="06"
                    label="Contatti"
                    title={
                        <>
                            Il modo più veloce? <em className="text-sky-400">Alzare il telefono.</em>
                        </>
                    }
                />

                <div className="mt-16 grid lg:grid-cols-2 gap-16">
                    <Reveal>
                        <p className="max-w-md text-slate-300 leading-relaxed">
                            Chiamaci o scrivici su WhatsApp: rispondiamo quasi sempre in
                            giornata. Se preferisci, compila il modulo e ti richiamiamo noi.
                        </p>
                        <div className="mt-10 space-y-6">
                            {SITE.tutors.map((t) => (
                                <div
                                    key={t.id}
                                    data-testid={`contact-tutor-${t.id}`}
                                    className="border border-slate-800 p-6 flex flex-wrap items-center justify-between gap-4 hover:border-sky-400/60 transition-colors duration-500"
                                >
                                    <div>
                                        <p className="font-serif text-2xl text-slate-50">{t.name}</p>
                                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500 mt-1">{t.role}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <a
                                            data-testid={`contact-call-${t.id}`}
                                            href={t.phoneHref}
                                            className="inline-flex items-center gap-2 font-mono text-sm text-slate-100 border border-slate-600 px-4 py-2 hover:border-sky-400 hover:text-sky-400 transition-colors duration-300"
                                        >
                                            <Phone size={14} /> {t.phoneDisplay}
                                        </a>
                                        <a
                                            data-testid={`contact-whatsapp-${t.id}`}
                                            href={t.whatsapp}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Scrivi a ${t.name} su WhatsApp`}
                                            className="inline-flex items-center justify-center w-10 h-10 border border-slate-600 text-slate-100 hover:border-sky-400 hover:text-sky-400 transition-colors duration-300"
                                        >
                                            <MessageCircle size={16} />
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <form
                            data-testid="contact-form"
                            onSubmit={submit}
                            className="border border-slate-800 bg-[#0F172A]/30 p-8 space-y-5"
                        >
                            <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
                                Ti richiamiamo noi
                            </p>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <input
                                    data-testid="contact-input-name"
                                    required
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder="Nome e cognome"
                                    className={inputCls}
                                />
                                <input
                                    data-testid="contact-input-phone"
                                    required
                                    value={form.phone}
                                    onChange={set("phone")}
                                    placeholder="Il tuo numero di telefono"
                                    className={inputCls}
                                />
                            </div>
                            <div className="grid sm:grid-cols-3 gap-5">
                                <select data-testid="contact-select-level" required value={form.level} onChange={set("level")} className={inputCls}>
                                    <option value="" disabled>Livello</option>
                                    <option value="medie">Medie</option>
                                    <option value="superiori">Superiori</option>
                                </select>
                                <select data-testid="contact-select-subject" required value={form.subject} onChange={set("subject")} className={inputCls}>
                                    <option value="" disabled>Materia</option>
                                    <option value="matematica">Matematica</option>
                                    <option value="fisica">Fisica</option>
                                    <option value="entrambe">Entrambe</option>
                                </select>
                                <select data-testid="contact-select-mode" required value={form.mode} onChange={set("mode")} className={inputCls}>
                                    <option value="" disabled>Modalità</option>
                                    <option value="online">Online</option>
                                    <option value="domicilio">A domicilio</option>
                                    <option value="indifferente">Indifferente</option>
                                </select>
                            </div>
                            <textarea
                                data-testid="contact-input-message"
                                value={form.message}
                                onChange={set("message")}
                                placeholder="Raccontaci in due righe di cosa hai bisogno (facoltativo)"
                                rows={4}
                                className={`${inputCls} resize-none`}
                            />
                            <button
                                data-testid="contact-submit-button"
                                type="submit"
                                disabled={sending}
                                className="w-full inline-flex items-center justify-center gap-3 bg-slate-50 text-[#020617] font-semibold px-8 py-4 hover:bg-sky-400 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
                            >
                                {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                                {sending ? "Invio in corso..." : "Invia la richiesta"}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
export default Contact;
